import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
} from '@mui/material';
import CategoryCard from '../../component/ConfigurationsPage/CategoryCard.jsx';
import ProductCard from '../../component/ConfigurationsPage/ProductCard.jsx';
import CategoryProductPopup from './child/CategoryProductPopup.jsx';
import EditImgModel from '../Locations/LocationDetails/EditImgModal.jsx';
import TimeSettingPopup from './child/TimeSettingPopup.jsx'; // Import TimeSettingPopup
import ClockIcon from './child/ClockIcon.jsx'; // Import ClockIcon
import './ConfigurationProductDisplay.css';
import axios from '../../axiosConfig.jsx';
import DefaultImg from '../../assets/img/default-restaurant-img.svg';

// Import @dnd-kit for drag and drop
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component - SEPARATE DRAG HANDLE FROM CARD
const SortableItem = ({
  item,
  index,
  isProductsView,
  isSubCategoriesView,
  isDraggable,
  activeMenuCard,
  setActiveMenuCard,
  ebrizaLinkedId,
  handleCategoryClick,
  handleSubCategoryClick,
  handleOpenRenameCategory,
  handleOpenImageDialog,
  handleOpenDeleteCategory,
  refreshDataAndUpdateUI,
  handleProductDelete,
  isAnyDragging,
  handleClockClick,
  itemAvailability
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.ProductId || item.CategoryId || index,
    disabled: !isDraggable
  });

  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle menu icon click - COMPLETELY separate from drag
  const handleMenuIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Stop event from bubbling to parent elements
    e.nativeEvent.stopImmediatePropagation();

    // Toggle menu state
    const newMenuState = activeMenuCard !== index;
    setActiveMenuCard(newMenuState ? index : null);
    setIsMenuOpen(newMenuState);
  };

  // Handle card click
  const handleCardClick = (e) => {
    // Don't navigate if dragging or if menu is open
    if (isAnyDragging || isMenuOpen) {
      return;
    }

    e.stopPropagation();
    if (isSubCategoriesView) {
      handleSubCategoryClick(item);
    } else if (!isProductsView) {
      handleCategoryClick(item);
    }
  };

  // Handle menu option click
  const handleMenuOptionClick = (e, handler) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    setActiveMenuCard(null);
    setIsMenuOpen(false);

    if (handler) {
      // Use setTimeout to ensure menu closes before handler executes
      setTimeout(() => handler(), 10);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && activeMenuCard === index) {
        setActiveMenuCard(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeMenuCard, index]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    cursor: isDragging ? 'grabbing' : 'pointer',
  };

  // Determine item type for clock icon
  const getItemType = () => {
    if (isProductsView) return 'product';
    if (isSubCategoriesView) return 'subcat';
    return 'cat';
  };

  // Check if item has schedule
  const hasSchedule = itemAvailability && itemAvailability[isProductsView?item.ProductId:item.CategoryId]?.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="sortable-item"
    >
      {/* Drag handle - separate element for dragging only */}
      {isDraggable && (
        <div
          className="drag-handle"
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '0px',
            left: '47%',
            zIndex: 1,
            cursor: 'grab',
            backgroundColor: 'rgba(255,255,255,0.7)',
            borderRadius: '4px'
          }}
        >
          <div className="drag-handle-indicator">⋮⋮</div>
        </div>
      )}

      <div
        className={`category-card-1 ${isDragging ? 'dragging' : ''}`}
        ref={menuRef}
        onClick={handleCardClick}
        style={{ position: 'relative' }}
      >
        {/* Clock Icon - positioned absolutely - SHOW FOR ALL VIEWS */}
        {!ebrizaLinkedId && (
          <ClockIcon
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              handleClockClick(
                item.ProductId || item.CategoryId || item.id,
                getItemType()
              );
            }}
            hasSchedule={hasSchedule}
            tooltip="Set availability schedule"
            size="small"
            sx={{
              position: 'absolute',
              zIndex: 2,
              color: 'black',
              ...(isProductsView ? {
                top: '2px',
                right: '50px',
                left: 'auto',
                borderRadius: '7px 0px 0px 7px',
                backgroundColor: '#fff',
                padding: '2px 1px'
              } : {
                top: '0px',
                left: '0px',
                borderRadius: '50%',
                padding: '2px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              })
            }}
          />
        )}

        {isProductsView ? (
          <>
            <ProductCard
              product={item}
              name1={item.name}
              imgUrl={item.imageUrl}
              price={item.priceVAT}
              fetchLatestDataAndMap={refreshDataAndUpdateUI}
              onDelete={handleProductDelete}
              isDraggable={isDraggable}
            />
          </>
        ) : (
          <>
            <CategoryCard
              name1={item.name}
              totalItems={item.cat_products?.length || item.sub_categories?.length || 0}
              totalSeen={0}
              imgUrl={item.imageUrl}
              onCardMenuClick={handleMenuIconClick}
              onCardClick={handleCardClick}
              isDraggable={isDraggable}
              showClockIcon={false} // We're handling clock icon separately
            />

            {/* MENU BOX FOR CATEGORIES - Positioned absolutely outside the drag context */}
            {activeMenuCard === index && !ebrizaLinkedId && (
              <div
                className="card-actions"
                style={{
                  position: 'absolute',
                  zIndex: 99999,
                  backgroundColor: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  padding: '0px 0',
                  minWidth: '160px',
                  pointerEvents: 'auto',
                  top: '40px',
                  right: '10px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
              >
                <div className="actions2">
                  <div
                    className="option"
                    style={{ padding: '8px 16px', cursor: 'pointer' }}
                    onClick={(e) => handleMenuOptionClick(e, () => {
                      handleOpenRenameCategory(item.CategoryId, item.name);
                    })}
                  >
                    <div className="left">
                      <img className="icons23" alt="" src="/icons-54.svg" />
                      <div className="edit">Rename</div>
                    </div>
                    <img className="icons24" alt="" src="/icons2.svg" />
                  </div>
                  <div
                    className="option2"
                    style={{ padding: '8px 16px', cursor: 'pointer' }}
                    onClick={(e) => handleMenuOptionClick(e, () => {
                      handleOpenImageDialog(item.CategoryId);
                    })}
                  >
                    <div className="left2">
                      <img className="icons27" alt="" src="/icons-56.svg" />
                      <div className="edit2">Change icon</div>
                    </div>
                    <img className="icons28" alt="" src="/icons2.svg" />
                  </div>
                  <div
                    className="option4"
                    style={{ padding: '8px 16px', cursor: 'pointer' }}
                    onClick={(e) => handleMenuOptionClick(e, () => {
                      handleOpenDeleteCategory(item.CategoryId);
                    })}
                  >
                    <div className="left3">
                      <img className="icons29" alt="" src="/icons-58.svg" />
                      <div className="edit3">Delete</div>
                    </div>
                    <img className="icons30" alt="" src="/icons2.svg" />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


const ConfigurationProductDisplayAdhoc = ({ productCategories, fetchLatestDataAndMap, syncToPos }) => {
  // State for navigation
  const [currentLevel, setCurrentLevel] = useState({
    type: 'categories',
    selectedCategory: null,
    selectedSubCategory: null,
    breadcrumbs: []
  });

  // State for UI interactions
  const [activeMenuCard, setActiveMenuCard] = useState(null);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openProductPopup, setOpenProductPopup] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);

  // New state for time setting
  const [timePopupOpen, setTimePopupOpen] = useState(false);
  const [selectedItemForTime, setSelectedItemForTime] = useState(null);
  const [itemAvailability, setItemAvailability] = useState({});

  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showProfileImage, setShowProfileImage] = useState(DefaultImg);

  // Local state to manage categories data
  const [categoriesData, setCategoriesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [isAnyDragging, setIsAnyDragging] = useState(false);

  const ebrizaLinkedId = localStorage.getItem('ebrizaLinkedId');
  const clientId = localStorage.getItem('partnerId');

  // Initialize sensors for drag and drop - REMOVE DELAY for better UX
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        // No delay - rely on the separate drag handle
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Initialize data when component mounts or prop changes
  useEffect(() => {
    if (productCategories) {
      const sortedData = sortItemsBySortId(productCategories || []);
      setCategoriesData(sortedData);
      updateSelectedCategoryWithFreshData();
    }
  }, [productCategories]);

  // Fetch availability data
  useEffect(() => {
    const fetchAvailability = async () => {
      const items = getCurrentDisplayItems();
      if (items.length === 0) return;

      try {
        const availabilityMap = {};
        const itemIds = [];
        const itemTypes = [];

        items.forEach(item => {
          const itemId = item.ProductId || item.CategoryId || item.id;
          if (itemId) {
            itemIds.push(itemId.toString());

            // Determine item type
            let itemType = 'cat';
            if (currentLevel.type === 'products') {
              itemType = 'product';
            } else if (currentLevel.type === 'subcategories') {
              itemType = 'subcat';
            } else if (currentLevel.type === 'categories') {
              itemType = 'cat';
            }
            itemTypes.push(itemType);
          }
        });

        if (itemIds.length > 0) {
          const response = await axios.get('/partners/item_availability/bulk', {
            params: {
              client_id: clientId,
              item_ids: itemIds.join(','),
              item_types: itemTypes.join(',')
            }
          });

          if (response.data.data && response.data.data.length > 0) {
            response.data.data.forEach(item => {
              availabilityMap[item.item_id] = item.available_time;
            });
          }
        }

        setItemAvailability(availabilityMap);
      } catch (error) {
        console.error('Error fetching bulk availability:', error);
        // Fallback to individual calls
        const availabilityMap = {};
        const items = getCurrentDisplayItems();

        for (const item of items) {
          try {
            const itemId = item.ProductId || item.CategoryId || item.id;
            if (!itemId) continue;

            let itemType = 'cat';
            if (currentLevel.type === 'products') {
              itemType = 'product';
            } else if (currentLevel.type === 'subcategories') {
              itemType = 'subcat';
            }

            const response = await axios.get('/partners/item_availability', {
              params: {
                client_id: clientId,
                item_id: itemId,
                item_type: itemType
              }
            });

            if (response.data.data && response.data.data.length > 0) {
              availabilityMap[itemId] = response.data.data[0].available_time;
            }
          } catch (individualError) {
            console.error(`Error fetching availability for item:`, individualError);
          }
        }
        setItemAvailability(availabilityMap);
      }
    };

    fetchAvailability();
  }, [categoriesData, currentLevel.type, clientId]);

  // Sort items by sortId
  const sortItemsBySortId = (items) => {
    if (!items || !Array.isArray(items)) return [];

    return [...items]
      .filter(item => item)
      .map((item, index) => ({
        ...item,
        sortId: item.sortId !== undefined ? item.sortId : index
      }))
      .sort((a, b) => a.sortId - b.sortId);
  };

  // Update selected category with fresh data from categoriesData
  const updateSelectedCategoryWithFreshData = useCallback(() => {
    if (currentLevel.selectedCategory && categoriesData.length > 0) {
      const freshCategory = categoriesData.find(
        cat => cat.CategoryId === currentLevel.selectedCategory.CategoryId
      );

      if (freshCategory) {
        setCurrentLevel(prev => ({
          ...prev,
          selectedCategory: freshCategory
        }));
      }
    }
  }, [currentLevel.selectedCategory, categoriesData]);

  // Handle clock icon click
  const handleClockClick = (itemId, itemType = 'cat') => {
    setSelectedItemForTime({
      id: itemId,
      type: itemType
    });
    setTimePopupOpen(true);
  };

  // Handle time save success
  const handleTimeSaveSuccess = (savedData) => {
    // Update local availability state
    setItemAvailability(prev => ({
      ...prev,
      [savedData.item_id]: savedData.available_time
    }));

    // Show success message
    console.log('Availability saved successfully');
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    setIsAnyDragging(false);
    setActiveMenuCard(null); // Close any open menus when dragging ends

    if (!over) return;
    if (active.id === over.id) return;

    try {
      const items = getCurrentDisplayItems();
      const oldIndex = items.findIndex(item =>
        (item.ProductId || item.CategoryId) === active.id
      );
      const newIndex = items.findIndex(item =>
        (item.ProductId || item.CategoryId) === over.id
      );

      if (oldIndex === -1 || newIndex === -1) return;

      let updatedItems = [];
      let itemsToUpdate = [];

      if (currentLevel.type === 'categories') {
        const reorderedCategories = arrayMove(categoriesData, oldIndex, newIndex);
        updatedItems = reorderedCategories.map((item, index) => ({
          ...item,
          sortId: index
        }));

        setCategoriesData(updatedItems);
        itemsToUpdate = updatedItems;

      } else if (currentLevel.type === 'subcategories') {
        const subCategories = arrayMove(currentLevel.selectedCategory?.sub_categories || [], oldIndex, newIndex);
        const updatedSubCategories = subCategories.map((item, index) => ({
          ...item,
          sortId: index
        }));

        const updatedCategory = {
          ...currentLevel.selectedCategory,
          sub_categories: updatedSubCategories
        };

        const updatedCategories = categoriesData.map(cat =>
          cat.CategoryId === updatedCategory.CategoryId ? updatedCategory : cat
        );

        setCategoriesData(updatedCategories);
        setCurrentLevel(prev => ({ ...prev, selectedCategory: updatedCategory }));
        itemsToUpdate = updatedSubCategories;

      } else if (currentLevel.type === 'products') {
        const targetCategoryId = currentLevel.selectedSubCategory
          ? currentLevel.selectedSubCategory.CategoryId
          : currentLevel.selectedCategory?.CategoryId;

        let targetCategory = categoriesData.find(cat => cat.CategoryId === targetCategoryId);

        if (!targetCategory && currentLevel.selectedCategory) {
          targetCategory = currentLevel.selectedCategory.sub_categories?.find(
            sub => sub.CategoryId === targetCategoryId
          );
        }

        if (!targetCategory) return;

        const products = arrayMove(targetCategory.cat_products || [], oldIndex, newIndex);
        const updatedProducts = products.map((item, index) => ({
          ...item,
          sortId: index
        }));

        if (currentLevel.selectedSubCategory) {
          const updatedSubCategory = {
            ...currentLevel.selectedSubCategory,
            cat_products: updatedProducts
          };

          const updatedCategories = categoriesData.map(cat => {
            if (cat.sub_categories) {
              return {
                ...cat,
                sub_categories: cat.sub_categories.map(sub =>
                  sub.CategoryId === updatedSubCategory.CategoryId ? updatedSubCategory : sub
                )
              };
            }
            return cat;
          });

          setCategoriesData(updatedCategories);
          setCurrentLevel(prev => ({ ...prev, selectedSubCategory: updatedSubCategory }));
          itemsToUpdate = updatedProducts;

        } else {
          const updatedCategory = {
            ...targetCategory,
            cat_products: updatedProducts
          };

          const updatedCategories = categoriesData.map(cat =>
            cat.CategoryId === updatedCategory.CategoryId ? updatedCategory : cat
          );

          setCategoriesData(updatedCategories);
          setCurrentLevel(prev => ({ ...prev, selectedCategory: updatedCategory }));
          itemsToUpdate = updatedProducts;
        }
      }

      await updateSortOrder(itemsToUpdate);

    } catch (error) {
      console.error('Error reordering items:', error);
      refreshDataAndUpdateUI();
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
    setIsAnyDragging(true);
    setActiveMenuCard(null); // Close any open menus when dragging starts
  };

  // Update sort order via API
  const updateSortOrder = async (items) => {
    try {
      const endpoint = '/partners/update_sort_order';

      const payload = items.map(item => ({
        id: item.ProductId || item.CategoryId,
        sortId: item.sortId,
        type: item.ProductId ? 'product' : 'category'
      }));

      await axios.put(endpoint, { items: payload });

      refreshDataAndUpdateUI();

    } catch (error) {
      console.error('Error updating sort order:', error);
      throw error;
    }
  };

  // Navigation handlers
  const navigateToCategories = () => {
    setCurrentLevel({
      type: 'categories',
      selectedCategory: null,
      selectedSubCategory: null,
      breadcrumbs: []
    });
    setActiveMenuCard(null);
  };

  const navigateToSubcategories = (category) => {
    const sortedSubCategories = sortItemsBySortId(category.sub_categories || []);
    const sortedCategory = {
      ...category,
      sub_categories: sortedSubCategories
    };

    setCurrentLevel({
      type: 'subcategories',
      selectedCategory: sortedCategory,
      selectedSubCategory: null,
      breadcrumbs: [{ label: 'Categories', onClick: navigateToCategories }]
    });
    setActiveMenuCard(null);
  };

  const navigateToProducts = (category, isSubCategory = false) => {
    const sortedProducts = sortItemsBySortId(category.cat_products || []);
    const sortedCategory = {
      ...category,
      cat_products: sortedProducts
    };

    const breadcrumbs = [{ label: 'Categories', onClick: navigateToCategories }];

    if (isSubCategory) {
      breadcrumbs.push({
        label: currentLevel.selectedCategory?.name || 'Subcategories',
        onClick: () => navigateToSubcategories(currentLevel.selectedCategory)
      });
    }

    setCurrentLevel({
      type: 'products',
      selectedCategory: isSubCategory ? currentLevel.selectedCategory : sortedCategory,
      selectedSubCategory: isSubCategory ? sortedCategory : null,
      breadcrumbs
    });
    setActiveMenuCard(null);
  };

  const navigateToEmptyCategory = (category) => {
    setCurrentLevel({
      type: 'empty',
      selectedCategory: category,
      selectedSubCategory: null,
      breadcrumbs: [{ label: 'Categories', onClick: navigateToCategories }]
    });
    setActiveMenuCard(null);
  };

  const handleBreadcrumbClick = (breadcrumb) => {
    if (breadcrumb.onClick) breadcrumb.onClick();
  };

  // Helper function to check if a category has NO items (no subcategories AND no products)
  const isEmptyCategory = (category) => {
    if (!category) return false;
    const hasNoSubCategories = !category.sub_categories || category.sub_categories.length === 0;
    const hasNoProducts = !category.cat_products || category.cat_products.length === 0;
    return hasNoSubCategories && hasNoProducts;
  };

  // Helper function to check if a category has only products (no subcategories)
  const hasOnlyProducts = (category) => {
    if (!category) return false;
    const hasNoSubCategories = !category.sub_categories || category.sub_categories.length === 0;
    const hasProducts = category.cat_products && category.cat_products.length > 0;
    return hasNoSubCategories && hasProducts;
  };

  // Helper function to check if a category has only subcategories (no products)
  const hasOnlySubCategories = (category) => {
    if (!category) return false;
    const hasSubCategories = category.sub_categories && category.sub_categories.length > 0;
    const hasNoProducts = !category.cat_products || category.cat_products.length === 0;
    return hasSubCategories && hasNoProducts;
  };

  // Helper function to check if a category has both subcategories and products
  const hasBothSubCategoriesAndProducts = (category) => {
    if (!category) return false;
    const hasSubCategories = category.sub_categories && category.sub_categories.length > 0;
    const hasProducts = category.cat_products && category.cat_products.length > 0;
    return hasSubCategories && hasProducts;
  };

  // Category click handler
  const handleCategoryClick = useCallback((category) => {
    if (isAnyDragging) return; // Don't navigate if dragging

    if (isEmptyCategory(category)) {
      navigateToEmptyCategory(category);
    } else if (hasOnlySubCategories(category)) {
      navigateToSubcategories(category);
    } else if (hasOnlyProducts(category)) {
      navigateToProducts(category);
    } else if (hasBothSubCategoriesAndProducts(category)) {
      navigateToSubcategories(category);
    } else {
      navigateToSubcategories(category);
    }
  }, [isAnyDragging]);

  // Subcategory click handler
  const handleSubCategoryClick = useCallback((subCategory) => {
    if (isAnyDragging) return; // Don't navigate if dragging
    navigateToProducts(subCategory, true);
  }, [isAnyDragging]);

  // Get current display items based on navigation level
  const getCurrentDisplayItems = () => {
    switch (currentLevel.type) {
      case 'categories':
        return categoriesData || [];
      case 'subcategories':
        return currentLevel.selectedCategory?.sub_categories || [];
      case 'products':
        const categoryId = currentLevel.selectedSubCategory
          ? currentLevel.selectedSubCategory.CategoryId
          : currentLevel.selectedCategory?.CategoryId;

        if (!categoryId || !categoriesData) return [];

        let targetCategory = null;

        targetCategory = categoriesData.find(cat => cat.CategoryId === categoryId);

        if (!targetCategory && currentLevel.selectedCategory) {
          targetCategory = currentLevel.selectedCategory.sub_categories?.find(
            sub => sub.CategoryId === categoryId
          );
        }

        return targetCategory?.cat_products || [];
      case 'empty':
        return [];
      default:
        return [];
    }
  };

  // Check if current view has subcategories
  const hasSubCategoriesInCurrentView = () => {
    if (currentLevel.type === 'subcategories') {
      const items = getCurrentDisplayItems();
      return items.length > 0;
    }
    return false;
  };

  // Check if current view has products
  const hasProductsInCurrentView = () => {
    if (currentLevel.type === 'products') {
      const items = getCurrentDisplayItems();
      return items.length > 0;
    } else if (currentLevel.type === 'empty' || currentLevel.type === 'subcategories') {
      return currentLevel.selectedCategory?.cat_products?.length > 0;
    }
    return false;
  };

  // Check which add buttons should be visible based on your conditions
  const getVisibleAddButtons = () => {
    const items = getCurrentDisplayItems();

    switch (currentLevel.type) {
      case 'categories':
        return { showAddCategory: true, showAddProduct: false };

      case 'subcategories':
        if (hasSubCategoriesInCurrentView()) {
          return { showAddCategory: true, showAddProduct: false };
        } else if (hasProductsInCurrentView()) {
          return { showAddCategory: false, showAddProduct: true };
        } else {
          return { showAddCategory: true, showAddProduct: true };
        }

      case 'products':
        return { showAddCategory: false, showAddProduct: true };

      case 'empty':
        return { showAddCategory: true, showAddProduct: true };

      default:
        return { showAddCategory: false, showAddProduct: false };
    }
  };

  // Dialog handlers
  const handleOpenAddCategory = (isSubCategory = false) => {
    if (ebrizaLinkedId) return;
    setCategoryName('');
    setSelectedCategoryId(null);
    setIsAddingSubCategory(isSubCategory);
    setOpenCategoryDialog(true);
  };

  const handleOpenRenameCategory = (categoryId, currentName) => {
    setActiveMenuCard(null);
    setSelectedCategoryId(categoryId);
    setCategoryName(currentName);
    setIsAddingSubCategory(false);
    setOpenCategoryDialog(true);
  };

  const handleOpenDeleteCategory = (categoryId) => {
    setActiveMenuCard(null);
    setCategoryToDelete(categoryId);
    setOpenDeleteDialog(true);
  };

  const handleOpenImageDialog = (categoryId) => {
    setActiveMenuCard(null);
    setSelectedCategoryId(categoryId);
    setOpenImageDialog(true);
  };

  const handleOpenAddProduct = () => {
    if (ebrizaLinkedId) return;
    setOpenProductPopup(true);
  };

  // Enhanced fetch function to update UI immediately
  const refreshDataAndUpdateUI = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchLatestDataAndMap();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchLatestDataAndMap]);

  // API handlers
  const handleSaveCategory = async () => {
    if (!categoryName) return;

    try {
      let payload = {};

      if (selectedCategoryId) {
        payload = { name: categoryName, CategoryId: selectedCategoryId };
      } else if (isAddingSubCategory && currentLevel.selectedCategory) {
        const currentSubCategories = currentLevel.selectedCategory.sub_categories || [];
        const sortId = currentSubCategories.length;

        payload = {
          name: categoryName,
          description: '',
          imageUrl: '',
          tileColor: '',
          parent_cat_id: currentLevel.selectedCategory.id,
          sortId: sortId
        };
      } else {
        const sortId = categoriesData.length;
        payload = {
          name: categoryName,
          description: '',
          imageUrl: '',
          tileColor: '',
          sortId: sortId
        };
      }

      const endpoint = '/partners/menu_categories';
      const method = selectedCategoryId ? 'put' : 'post';

      const response = await axios[method](endpoint, payload);

      if (response.data.success) {
        if (method === 'post' && response.data.data && response.data.data[0]) {
          const newCategory = response.data.data[0];

          if (isAddingSubCategory && currentLevel.selectedCategory) {
            const updatedCategories = categoriesData.map(cat => {
              if (cat.CategoryId === currentLevel.selectedCategory.CategoryId) {
                return {
                  ...cat,
                  sub_categories: [...(cat.sub_categories || []), newCategory]
                };
              }
              return cat;
            });
            setCategoriesData(updatedCategories);

            const updatedSelectedCategory = {
              ...currentLevel.selectedCategory,
              sub_categories: [...(currentLevel.selectedCategory.sub_categories || []), newCategory]
            };

            setCurrentLevel(prev => ({
              ...prev,
              selectedCategory: updatedSelectedCategory
            }));

            setTimeout(() => {
              navigateToSubcategories(updatedSelectedCategory);
            }, 300);
          } else {
            setCategoriesData(prev => [...prev, newCategory]);
          }
        }

        setOpenCategoryDialog(false);
        setCategoryName('');
        setSelectedCategoryId(null);
        setIsAddingSubCategory(false);

        refreshDataAndUpdateUI();
      }
    } catch (error) {
      console.error('Error saving category:', error);
      refreshDataAndUpdateUI();
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await axios.delete('/partners/menu_categories', {
        data: { CategoryId: categoryToDelete }
      });

      if (response.data.success) {
        const updatedCategories = categoriesData.filter(
          cat => cat.CategoryId !== categoryToDelete
        );
        setCategoriesData(updatedCategories);

        if (currentLevel.selectedCategory?.CategoryId === categoryToDelete) {
          navigateToCategories();
        } else if (currentLevel.type === 'subcategories' || currentLevel.type === 'empty') {
          const updatedSelectedCategory = {
            ...currentLevel.selectedCategory,
            sub_categories: currentLevel.selectedCategory.sub_categories?.filter(
              sub => sub.CategoryId !== categoryToDelete
            ) || []
          };

          setCurrentLevel(prev => ({
            ...prev,
            selectedCategory: updatedSelectedCategory
          }));

          if (isEmptyCategory(updatedSelectedCategory)) {
            navigateToEmptyCategory(updatedSelectedCategory);
          }
        }

        setOpenDeleteDialog(false);
        setCategoryToDelete(null);

        refreshDataAndUpdateUI();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      refreshDataAndUpdateUI();
    }
  };

  const handleSaveImage = async (uploadedUrl) => {
    if (!uploadedUrl || !selectedCategoryId) {
      setOpenImageDialog(false);
      return;
    }

    try {
      const payload = {
        CategoryId: selectedCategoryId,
        imageUrl: uploadedUrl
      };

      const response = await axios.put('/partners/menu_categories', payload);

      if (response.data.success) {
        const updatedCategories = categoriesData.map(cat => {
          if (cat.CategoryId === selectedCategoryId) {
            return { ...cat, imageUrl: uploadedUrl };
          }
          return cat;
        });
        setCategoriesData(updatedCategories);

        setOpenImageDialog(false);
        refreshDataAndUpdateUI();
      }
    } catch (error) {
      console.error('Error saving image:', error);
    }
  };

  // Handle product operations
  const handleProductAdd = () => {
    refreshDataAndUpdateUI();

    setTimeout(() => {
      if (currentLevel.type === 'empty') {
        const updatedCategory = categoriesData.find(cat =>
          cat.CategoryId === currentLevel.selectedCategory.CategoryId
        );
        if (updatedCategory && hasOnlyProducts(updatedCategory)) {
          navigateToProducts(updatedCategory);
        }
      }
    }, 500);
  };

  const handleProductDelete = () => {
    refreshDataAndUpdateUI();

    setTimeout(() => {
      const selectedCat = currentLevel.selectedCategory;
      if (selectedCat && isEmptyCategory(selectedCat)) {
        navigateToEmptyCategory(selectedCat);
      }
    }, 500);
  };

  // Render breadcrumbs
  const renderBreadcrumbs = () => {
    const breadcrumbs = [...currentLevel.breadcrumbs];

    if (currentLevel.type === 'products') {
      breadcrumbs.push({
        label: currentLevel.selectedSubCategory?.name || currentLevel.selectedCategory?.name || 'Products',
        isActive: true
      });
    } else if (currentLevel.type === 'subcategories') {
      breadcrumbs.push({
        label: currentLevel.selectedCategory?.name || 'Subcategories',
        isActive: true
      });
    } else if (currentLevel.type === 'empty') {
      breadcrumbs.push({
        label: currentLevel.selectedCategory?.name || 'Empty',
        isActive: true
      });
    } else {
      breadcrumbs.push({ label: 'Categories', isActive: true });
    }

    return (
      <div className="bread-counts">
        {breadcrumbs.map((crumb, index) => (
          <div key={index} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {index > 0 && <span style={{ margin: '0 10px' }}>&gt;</span>}
            <a
              className={crumb.isActive ? 'active' : 'categories1'}
              onClick={crumb.onClick ? () => handleBreadcrumbClick(crumb) : undefined}
              style={{
                cursor: crumb.onClick ? 'pointer' : 'default',
                textTransform: 'capitalize'
              }}
            >
              {crumb.label}
            </a>
          </div>
        ))}
      </div>
    );
  };

  // Get the active item for drag overlay
  const getActiveItem = () => {
    const items = getCurrentDisplayItems();
    return items.find(item =>
      (item.ProductId || item.CategoryId) === activeDragId
    );
  };

  // Render cards with @dnd-kit
  const renderCards = () => {
    const { showAddCategory, showAddProduct } = getVisibleAddButtons();
    const items = getCurrentDisplayItems();
    const isProductsView = currentLevel.type === 'products';
    const isSubCategoriesView = currentLevel.type === 'subcategories';
    const isDraggable = !ebrizaLinkedId;
    const activeItem = getActiveItem();

    const itemIds = items.map(item => item.ProductId || item.CategoryId);

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
      >
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <div className="cards">
            {isLoading ? (
              <div className="loading-indicator">Loading...</div>
            ) : (
              <>
                {items.map((item, index) => (
                  <SortableItem
                    key={item.ProductId || item.CategoryId || index}
                    item={item}
                    index={index}
                    isProductsView={isProductsView}
                    isSubCategoriesView={isSubCategoriesView}
                    isDraggable={isDraggable}
                    activeMenuCard={activeMenuCard}
                    setActiveMenuCard={setActiveMenuCard}
                    ebrizaLinkedId={ebrizaLinkedId}
                    handleCategoryClick={handleCategoryClick}
                    handleSubCategoryClick={handleSubCategoryClick}
                    handleOpenRenameCategory={handleOpenRenameCategory}
                    handleOpenImageDialog={handleOpenImageDialog}
                    handleOpenDeleteCategory={handleOpenDeleteCategory}
                    refreshDataAndUpdateUI={refreshDataAndUpdateUI}
                    handleProductDelete={handleProductDelete}
                    isAnyDragging={isAnyDragging}
                    handleClockClick={handleClockClick}
                    itemAvailability={itemAvailability}
                  />
                ))}

                {showAddCategory && (
                  <div
                    onClick={() => {
                      if (!isAnyDragging) {
                        handleOpenAddCategory(currentLevel.type === 'subcategories' || currentLevel.type === 'empty');
                      }
                    }}
                    style={ebrizaLinkedId ? {
                      cursor: 'not-allowed',
                      opacity: 0.5
                    } : {
                      cursor: 'pointer'
                    }}
                    className="category-card71"
                  >
                    <img className="icons22" alt="" src="/icons1.svg" />
                    <div className="add-category7">
                      {currentLevel.type === 'subcategories' || currentLevel.type === 'empty' ? 'Add Subcategory' : 'Add Category'}
                    </div>
                  </div>
                )}

                {showAddProduct && (
                  <div
                    onClick={() => {
                      if (!isAnyDragging) {
                        handleOpenAddProduct();
                      }
                    }}
                    style={ebrizaLinkedId ? {
                      cursor: 'not-allowed',
                      opacity: 0.5
                    } : {
                      cursor: 'pointer'
                    }}
                    className="category-card71"
                  >
                    <img className="icons22" alt="" src="/icons1.svg" />
                    <div className="add-category7">Add Product</div>
                  </div>
                )}
              </>
            )}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <div className="category-card-1 dragging">
              {isProductsView ? (
                <>
                  <ClockIcon
                    onClick={() => { }}
                    hasSchedule={itemAvailability[activeItem.ProductId]?.length > 0}
                    tooltip="Set availability schedule"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '-3px',
                      left: '-3px',
                      zIndex: 2,
                      color: 'black',
                    }}
                  />
                  <ProductCard
                    product={activeItem}
                    name1={activeItem.name}
                    imgUrl={activeItem.imageUrl}
                    price={activeItem.priceVAT}
                    fetchLatestDataAndMap={refreshDataAndUpdateUI}
                    onDelete={handleProductDelete}
                    isDraggable={false}
                  />
                </>
              ) : (
                <>
                  <ClockIcon
                    onClick={() => { }}
                    hasSchedule={itemAvailability[activeItem.CategoryId]?.length > 0}
                    tooltip="Set availability schedule"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '-3px',
                      left: '-3px',
                      zIndex: 2,
                      color: 'black',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      padding: '2px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}
                  />
                  <CategoryCard
                    name1={activeItem.name}
                    totalItems={activeItem.cat_products?.length || activeItem.sub_categories?.length || 0}
                    totalSeen={0}
                    imgUrl={activeItem.imageUrl}
                    onCardMenuClick={() => { }}
                    onCardClick={() => { }}
                    isDraggable={false}
                  />
                </>
              )}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    );
  };

  // Get dialog title based on context
  const getDialogTitle = () => {
    if (selectedCategoryId) {
      return 'Rename Category';
    } else if (isAddingSubCategory) {
      return 'Add Subcategory';
    } else {
      return 'Add Category';
    }
  };

  return (
    <section className="top-bar2">
      <div className="inner-content-wrapper">
        <div className="inner-content">
          <div className="content3" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
            <button
              className="button-base12"
              onClick={(e) => {
                e.preventDefault();
                syncToPos();
              }}
            >
              <img className="icons12" alt="" src="/icons-11.svg" />
              <div className="text7">Sync to POS</div>
            </button>
          </div>

          <div className="categories">
            <div className="bread-countsactions">
              {renderBreadcrumbs()}

              <div className="buttons2 sync-button-cat">
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={refreshDataAndUpdateUI}
                  className="button-base14"
                >
                  <img className="icons14" alt="" src="/icons-121.svg" />
                </div>
              </div>
            </div>

            {renderCards()}
          </div>
        </div>
      </div>

      <EditImgModel
        profileImgOpen={openImageDialog}
        handleClose={handleSaveImage}
        setShowProfileImage={setShowProfileImage}
      />

      {/* Time Setting Popup */}
      <TimeSettingPopup
        open={timePopupOpen}
        onClose={() => setTimePopupOpen(false)}
        itemId={selectedItemForTime?.id}
        itemType={selectedItemForTime?.type || 'cat'}
        currentAvailability={selectedItemForTime?.id ? itemAvailability[selectedItemForTime.id] : []}
        onSaveSuccess={handleTimeSaveSuccess}
        clientId={clientId}
      />

      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)}>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={isAddingSubCategory ? "Subcategory Name" : "Category Name"}
            type="text"
            fullWidth
            variant="standard"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCategory}>
            {selectedCategoryId ? 'Rename' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this category?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} color="primary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {currentLevel.selectedCategory && (
        <CategoryProductPopup
          open={openProductPopup}
          onClose={() => setOpenProductPopup(false)}
          categories={categoriesData}
          activeCategory={currentLevel.selectedSubCategory || currentLevel.selectedCategory}
          fetchLatestDataAndMap={refreshDataAndUpdateUI}
          onProductAdded={handleProductAdd}
        />
      )}
    </section>
  );
};

export default ConfigurationProductDisplayAdhoc;